const styles = {
  INFO: "color: #3B82F6; font-weight: bold;",
  SUCCESS: "color: #10B981; font-weight: bold;",
  WARN: "color: #F59E0B; font-weight: bold;",
  ERROR: "color: #EF4444; font-weight: bold;"
};
const createLogEntry = (level, message, additionalData, includeStack = false) => {
  const timestamp = (/* @__PURE__ */ new Date()).toISOString();
  let logMessage;
  let logData = additionalData;
  if (typeof message === "string") {
    logMessage = message;
  } else if (typeof message === "object" && message !== null) {
    logMessage = "Object logged";
    logData = message;
  } else {
    logMessage = String(message);
  }
  const entry = {
    timestamp,
    level,
    message: logMessage
  };
  if (logData !== void 0) {
    entry.data = logData;
  }
  if (includeStack) {
    const stack = new Error().stack;
    if (stack) {
      entry.stack = stack.split("\n").slice(3).join("\n");
    }
  }
  if (typeof window !== "undefined") {
    entry.context = {
      url: window.location.href,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    };
  }
  return entry;
};
const logWithStyle = (consoleMethod, level, entry) => {
  console[consoleMethod](`%c[${level}]`, styles[level], entry);
};
const logger = {
  info: (message, data) => {
    const entry = createLogEntry("INFO", message, data);
    logWithStyle("log", "INFO", entry);
  },
  success: (message, data) => {
    const entry = createLogEntry("SUCCESS", message, data);
    logWithStyle("log", "SUCCESS", entry);
  },
  warn: (message, data) => {
    const entry = createLogEntry("WARN", message, data, true);
    logWithStyle("warn", "WARN", entry);
  },
  error: (message, data) => {
    const entry = createLogEntry("ERROR", message, data, true);
    logWithStyle("error", "ERROR", entry);
  }
};
function debounce(func, wait2, immediate = false) {
  let timeout = null;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait2);
    if (callNow) func(...args);
  };
}
function delegate(target, selector, event, handler) {
  const wrappedHandler = (e) => {
    const targetElement = e.target;
    const delegateTarget = targetElement.closest(selector);
    if (delegateTarget && target.contains(delegateTarget)) {
      handler(e, delegateTarget);
    }
  };
  target.addEventListener(event, wrappedHandler);
  return () => {
    target.removeEventListener(event, wrappedHandler);
  };
}
class StorageHelper {
  storage = null;
  type;
  isAvailable = false;
  constructor(type) {
    this.type = type;
    this.checkAvailability();
  }
  /**
   * Check if storage is available (handles private browsing, etc.)
   */
  checkAvailability() {
    try {
      const storage = window[this.type];
      const testKey = "__storage_test__";
      storage.setItem(testKey, "test");
      storage.removeItem(testKey);
      this.storage = storage;
      this.isAvailable = true;
    } catch (e) {
      this.isAvailable = false;
      logger.warn(`${this.type} is not available`, { error: e });
    }
  }
  /**
   * Set an item in storage
   */
  set(key, value, expiresInMinutes) {
    if (!this.isAvailable || !this.storage) return false;
    try {
      const item = { value };
      if (expiresInMinutes) {
        item.expires = Date.now() + expiresInMinutes * 60 * 1e3;
      }
      this.storage.setItem(key, JSON.stringify(item));
      return true;
    } catch (e) {
      logger.error("Failed to set storage item", { key, error: e });
      return false;
    }
  }
  /**
   * Get an item from storage
   */
  get(key) {
    if (!this.isAvailable || !this.storage) return null;
    try {
      const raw = this.storage.getItem(key);
      if (!raw) return null;
      const item = JSON.parse(raw);
      if (item.expires && Date.now() > item.expires) {
        this.remove(key);
        return null;
      }
      return item.value;
    } catch (e) {
      logger.error("Failed to get storage item", { key, error: e });
      return null;
    }
  }
  /**
   * Remove an item from storage
   */
  remove(key) {
    if (!this.isAvailable || !this.storage) return false;
    try {
      this.storage.removeItem(key);
      return true;
    } catch (e) {
      logger.error("Failed to remove storage item", { key, error: e });
      return false;
    }
  }
  /**
   * Clear all items from storage
   */
  clear() {
    if (!this.isAvailable || !this.storage) return false;
    try {
      this.storage.clear();
      return true;
    } catch (e) {
      logger.error("Failed to clear storage", { error: e });
      return false;
    }
  }
  /**
   * Check if a key exists in storage
   */
  has(key) {
    if (!this.isAvailable || !this.storage) return false;
    return this.storage.getItem(key) !== null;
  }
  /**
   * Get all keys in storage
   */
  keys() {
    if (!this.isAvailable || !this.storage) return [];
    const keys = [];
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key) keys.push(key);
    }
    return keys;
  }
  /**
   * Get storage size in bytes (approximate)
   */
  size() {
    if (!this.isAvailable || !this.storage) return 0;
    let total = 0;
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key) {
        const value = this.storage.getItem(key);
        if (value) {
          total += key.length + value.length;
        }
      }
    }
    return total;
  }
  /**
   * Create a namespaced storage helper
   * Prefixes all keys with the namespace
   */
  namespace(prefix) {
    return new NamespacedStorage(this, prefix);
  }
  /**
   * Check if storage is available
   */
  get available() {
    return this.isAvailable;
  }
}
class NamespacedStorage {
  constructor(storage, prefix) {
    this.storage = storage;
    this.prefix = prefix;
  }
  getKey(key) {
    return `${this.prefix}:${key}`;
  }
  set(key, value, expiresInMinutes) {
    return this.storage.set(this.getKey(key), value, expiresInMinutes);
  }
  get(key) {
    return this.storage.get(this.getKey(key));
  }
  remove(key) {
    return this.storage.remove(this.getKey(key));
  }
  has(key) {
    return this.storage.has(this.getKey(key));
  }
  /**
   * Clear all items in this namespace
   */
  clear() {
    const keys = this.storage.keys().filter((key) => key.startsWith(`${this.prefix}:`));
    let success = true;
    keys.forEach((key) => {
      if (!this.storage.remove(key)) {
        success = false;
      }
    });
    return success;
  }
  /**
   * Get all keys in this namespace (without prefix)
   */
  keys() {
    const prefixLength = this.prefix.length + 1;
    return this.storage.keys().filter((key) => key.startsWith(`${this.prefix}:`)).map((key) => key.substring(prefixLength));
  }
}
const local = new StorageHelper("localStorage");
new StorageHelper("sessionStorage");
const CONSENT_STORAGE_KEY$2 = "cookieSelections";
const isConsentGranted = () => {
  const selections = local.get(CONSENT_STORAGE_KEY$2);
  return selections?.analytics === true;
};
const idleInject = (inject) => {
  if ("requestIdleCallback" in window) {
    requestIdleCallback(inject);
  } else {
    setTimeout(inject, 0);
  }
};
const buildGtmSrc = ({ containerId, serverUrl, scriptName }) => {
  if (serverUrl && scriptName) {
    const encodedId = btoa(`id=${containerId}`).replace(/=+$/, "");
    return `${serverUrl}/${scriptName}?P=${encodedId}&page=1`;
  }
  return `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
};
class ThemeScripts {
  /** Loads a script according to its strategy. TypeScript narrows to `GtmScriptConfig` when `id: 'gtm'`. */
  load = (config) => {
    if (config.id === "gtm") {
      this.loadGtm(config);
      return;
    }
    const { id, strategy, src, inline, async: isAsync = true, defer = false, attributes = {}, onLoad, onError } = config;
    this.applyStrategy({
      strategy,
      inject: () => this.inject({ id, src, inline, isAsync, defer, attributes, onLoad, onError })
    });
  };
  /** Handles GTM-specific setup: dataLayer push, URL building, noscript fallback. */
  loadGtm = (config) => {
    const { containerId } = config.gtmConfig;
    this.applyStrategy({
      strategy: config.strategy,
      inject: () => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ "gtm.start": (/* @__PURE__ */ new Date()).getTime(), event: "gtm.js" });
        this.inject({
          id: "gtm",
          src: buildGtmSrc(config.gtmConfig),
          isAsync: true,
          defer: false,
          attributes: { "data-gtm": containerId },
          onLoad: config.onLoad,
          onError: config.onError
        });
        if (!document.querySelector(`noscript[data-gtm="${containerId}"]`)) {
          const noscript = document.createElement("noscript");
          noscript.dataset.gtm = containerId;
          const iframe = document.createElement("iframe");
          iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
          iframe.height = "0";
          iframe.width = "0";
          iframe.style.cssText = "display:none;visibility:hidden";
          noscript.appendChild(iframe);
          document.body.insertAdjacentElement("afterbegin", noscript);
        }
      }
    });
  };
  applyStrategy = ({ strategy, inject }) => {
    switch (strategy) {
      case "immediate": {
        inject();
        break;
      }
      case "on-default-consent-set": {
        window.addEventListener("edition:consent-default-set", () => idleInject(inject), { once: true });
        break;
      }
      case "on-consent-granted": {
        window.addEventListener(
          "edition:consent-default-set",
          () => {
            if (isConsentGranted()) {
              idleInject(inject);
              return;
            }
            const waitForConsent = () => {
              if (!isConsentGranted()) return;
              idleInject(inject);
              window.removeEventListener("edition:consent-updated", waitForConsent);
            };
            window.addEventListener("edition:consent-updated", waitForConsent);
          },
          { once: true }
        );
        break;
      }
      case "idle": {
        idleInject(inject);
        break;
      }
      default: {
        const _exhaustive = strategy;
        console.warn("[ThemeScripts] Unknown strategy:", _exhaustive);
      }
    }
  };
  inject = ({ id, src, inline, isAsync, defer, attributes, onLoad, onError }) => {
    if (document.querySelector(`script[data-script-id="${id}"]`)) {
      return;
    }
    if (inline) {
      inline();
    }
    if (!src) {
      return;
    }
    const script = document.createElement("script");
    script.async = isAsync;
    script.defer = defer;
    script.src = src;
    script.dataset.scriptId = id;
    Object.entries(attributes).forEach(([key, value]) => {
      script.setAttribute(key, value);
    });
    if (onLoad) {
      script.addEventListener("load", onLoad);
    }
    if (onError) {
      script.addEventListener("error", onError);
    }
    document.head.appendChild(script);
  };
}
const LOG_STYLES = {
  header: "background:#4f46e5;color:#fff;padding:2px 8px;border-radius:3px;font-weight:bold",
  event: "background:#10b981;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold;margin-left:4px",
  context: "background:#0ea5e9;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold;margin-left:4px",
  consent: "background:#8b5cf6;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold;margin-left:4px",
  init: "background:#f59e0b;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold;margin-left:4px",
  config: "background:#6b7280;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold;margin-left:4px",
  label: "color:inherit;font-weight:bold;margin-left:4px",
  muted: "color:#6b7280;font-style:italic;margin-left:4px"
};
const CATEGORY_LABELS = {
  event: "Event",
  context: "Context",
  consent: "Consent",
  init: "Init",
  config: "Config"
};
const debugLog = ({ category, label, payload }) => {
  const timestamp = (/* @__PURE__ */ new Date()).toISOString();
  const displayTime = timestamp.split("T")[1].replace("Z", "");
  const categoryStyle = LOG_STYLES[category];
  console.groupCollapsed("%cEdition DataLayer%c%s%c%s%c%s", LOG_STYLES.header, categoryStyle, ` ${CATEGORY_LABELS[category]}`, LOG_STYLES.label, ` ${label}`, LOG_STYLES.muted, ` ${displayTime}`);
  if (payload !== void 0) console.log(payload);
  console.groupEnd();
};
const toAbsoluteUrl = (url) => {
  if (!url) {
    return null;
  }
  if (url.startsWith("//")) {
    return `https:${url}`;
  }
  if (url.startsWith("/")) {
    return `${window.location.origin}${url}`;
  }
  return url;
};
const CONSENT_STORAGE_KEY$1 = "cookieSelections";
const CONSENT_DENIED_DEFAULTS = { personalization: false, marketing: false, analytics: false };
const CONSENT_KEYS = ["personalization", "marketing", "analytics"];
class CookieBanner extends HTMLElement {
  consentApiReady = false;
  async connectedCallback() {
    await this.setupConsent();
    this.addEventListeners();
    this.activateTab("consent");
    this.syncCheckboxGroups();
    if (this.dataset.forceShow === "true" || await this.shouldShowBanner()) {
      this.showBanner();
    }
  }
  /** Sets gtag consent defaults, fires `edition:consent-default-set`, and optionally loads GTM. */
  setupConsent = async () => {
    this.ensureGtag();
    const selections = local.get(CONSENT_STORAGE_KEY$1) ?? CONSENT_DENIED_DEFAULTS;
    window.gtag?.("consent", "default", {
      functionality_storage: "granted",
      analytics_storage: selections.analytics ? "granted" : "denied",
      personalization_storage: selections.personalization ? "granted" : "denied",
      ad_personalization: selections.personalization ? "granted" : "denied",
      ad_storage: selections.marketing ? "granted" : "denied",
      ad_user_data: selections.marketing ? "granted" : "denied",
      wait_for_update: 3e3
    });
    window.gtag?.("set", "ads_data_redaction", !selections.marketing);
    window.gtag?.("set", "url_passthrough", true);
    window.dispatchEvent(new CustomEvent("edition:consent-default-set"));
    await this.loadShopifyConsentApi();
  };
  addEventListeners = () => {
    this.querySelector("#tab-consent")?.addEventListener("click", () => this.activateTab("consent"));
    this.querySelector("#tab-details")?.addEventListener("click", () => this.activateTab("details"));
    this.querySelector(".allow-all-btn")?.addEventListener("click", this.handleAllowAll);
    this.querySelector(".allow-selected-btn")?.addEventListener("click", this.handleAllowSelected);
    this.querySelector('[data-action="reject"]')?.addEventListener("click", this.handleRejectAll);
    document.getElementById("manage-cookie-consent")?.addEventListener("click", (event) => {
      event.preventDefault();
      this.showBanner();
    });
  };
  activateTab = (tab) => {
    ["consent", "details"].forEach((id) => {
      this.querySelector(`#tab-${id}`)?.classList.toggle("active", id === tab);
      this.querySelector(`#panel-${id}`)?.classList.toggle("active", id === tab);
      this.querySelector(`#panel-${id}`)?.classList.toggle("tw:hidden!", id !== tab);
    });
  };
  /**
   * Returns true if the banner should be shown.
   * Prefers localStorage over the Shopify API — if the user has a stored selection we trust it
   * and skip the async API call entirely.
   */
  shouldShowBanner = async () => {
    if (local.get(CONSENT_STORAGE_KEY$1)) {
      return false;
    }
    if (!this.consentApiReady || !window.Shopify?.customerPrivacy?.currentVisitorConsent) {
      return true;
    }
    try {
      const { preferences, analytics, marketing } = await window.Shopify.customerPrivacy.currentVisitorConsent();
      return [preferences, analytics, marketing].every((v) => (v ?? "") === "");
    } catch {
      return true;
    }
  };
  loadShopifyConsentApi = () => {
    if (!window.Shopify?.loadFeatures) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      try {
        window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], (error) => {
          if (error) {
            logger.error("Shopify consent-tracking-api failed to load", error);
            resolve();
            return;
          }
          this.consentApiReady = true;
          resolve();
          document.addEventListener("visitorConsentCollected", async () => {
            try {
              const v = await window.Shopify?.customerPrivacy?.currentVisitorConsent?.();
              if (!v) {
                return;
              }
              this.setConsentGroup({ key: "personalization", checked: v.preferences === "yes" });
              this.setConsentGroup({ key: "marketing", checked: v.marketing === "yes" });
              this.setConsentGroup({ key: "analytics", checked: v.analytics === "yes" });
              this.saveSelections();
            } catch (err) {
              logger.error("visitorConsentCollected handler error", err);
            }
          });
        });
      } catch (e) {
        logger.error("Shopify loadFeatures threw", e);
        resolve();
      }
    });
  };
  /** Updates gtag consent, persists to localStorage, and fires `edition:consent-updated`. */
  applyConsent = ({ consent, hide }) => {
    window.gtag?.("consent", "update", {
      functionality_storage: "granted",
      analytics_storage: consent.analytics === "yes" ? "granted" : "denied",
      personalization_storage: consent.preferences === "yes" ? "granted" : "denied",
      ad_personalization: consent.preferences === "yes" ? "granted" : "denied",
      ad_storage: consent.marketing === "yes" ? "granted" : "denied",
      ad_user_data: consent.marketing === "yes" ? "granted" : "denied"
    });
    window.gtag?.("set", "ads_data_redaction", consent.marketing !== "yes");
    this.saveSelections();
    window.dispatchEvent(new CustomEvent("edition:consent-updated"));
    window.Shopify?.customerPrivacy?.setTrackingConsent?.({
      analytics: consent.analytics === "yes",
      marketing: consent.marketing === "yes",
      preferences: consent.preferences === "yes"
    });
    if (hide) {
      this.hideBanner();
    }
  };
  handleAllowAll = () => {
    this.querySelectorAll("input.ed-checkbox[data-consent]").forEach((cb) => {
      if (!cb.disabled) {
        cb.checked = true;
      }
    });
    this.applyConsent({ consent: { preferences: "yes", marketing: "yes", analytics: "yes" }, hide: true });
  };
  handleAllowSelected = () => {
    const { personalization, marketing, analytics } = this.getSelections();
    this.applyConsent({
      consent: {
        preferences: personalization ? "yes" : "no",
        marketing: marketing ? "yes" : "no",
        analytics: analytics ? "yes" : "no"
      },
      hide: true
    });
  };
  handleRejectAll = () => {
    this.applyConsent({ consent: { preferences: "no", marketing: "no", analytics: "no" }, hide: true });
  };
  /** Keeps the consent/details tab panels in sync — the same consent key can appear in both tabs. */
  syncCheckboxGroups = () => {
    const checkboxes = Array.from(this.querySelectorAll("input.ed-checkbox[data-consent]"));
    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", ({ currentTarget }) => {
        const source = currentTarget;
        checkboxes.forEach((other) => {
          if (other !== source && other.dataset.consent === source.dataset.consent && !other.disabled) {
            other.checked = source.checked;
          }
        });
      });
    });
    const grouped = checkboxes.reduce((acc, cb) => {
      const key = cb.dataset.consent;
      (acc[key] ??= []).push(cb);
      return acc;
    }, {});
    Object.values(grouped).forEach(([source, ...rest]) => {
      rest.forEach((cb) => {
        if (!cb.disabled) {
          cb.checked = source.checked;
        }
      });
    });
  };
  setConsentGroup = ({ key, checked }) => {
    this.querySelectorAll(`input.ed-checkbox[data-consent="${key}"]`).forEach((cb) => {
      if (!cb.disabled) {
        cb.checked = checked;
      }
    });
  };
  saveSelections = () => {
    local.set(CONSENT_STORAGE_KEY$1, this.getSelections());
  };
  getSelections = () => CONSENT_KEYS.reduce(
    (acc, key) => ({
      ...acc,
      [key]: !!this.querySelector(`input.ed-checkbox[data-consent="${key}"]`)?.checked
    }),
    { personalization: false, marketing: false, analytics: false }
  );
  get overlay() {
    return this.querySelector("[data-cookie-overlay]") ?? this;
  }
  /**
   * Opens the banner. Applies any previously saved selections to the checkboxes first —
   * if nothing is stored, the HTML defaults remain as-is.
   * Can be triggered externally via `#manage-cookie-consent`.
   */
  showBanner = () => {
    const saved = local.get(CONSENT_STORAGE_KEY$1);
    if (saved) {
      CONSENT_KEYS.forEach((key) => this.setConsentGroup({ key, checked: saved[key] }));
    }
    this.overlay.removeAttribute("hidden");
    this.overlay.classList.remove("tw:hidden!");
  };
  hideBanner = () => {
    this.overlay.classList.add("tw:hidden!");
  };
  /** Creates a `window.gtag` shim that queues calls into `dataLayer` if the real gtag hasn't loaded yet. */
  ensureGtag = () => {
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function gtag() {
        window.dataLayer.push(arguments);
      };
    }
  };
}
const sha256 = async (value) => {
  if (!value) {
    return null;
  }
  try {
    const encoded = new TextEncoder().encode(value);
    const hashBuffer = await crypto.subtle.digest("SHA-256", encoded);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
};
const resolveUrl = (input) => {
  if (typeof input === "string") {
    return input;
  }
  if (input instanceof URL) {
    return input.pathname;
  }
  return input.url;
};
const activeCurrency = () => window.Shopify?.currency?.active ?? "";
const parseRequestQuantity = (init) => {
  const body = init?.body;
  if (!body) {
    return 1;
  }
  try {
    if (typeof body === "string") {
      const parsed = JSON.parse(body);
      return Number(parsed.quantity) || 1;
    }
    if (body instanceof URLSearchParams) {
      return Number(body.get("quantity")) || 1;
    }
    if (body instanceof FormData) {
      return Number(body.get("quantity")) || 1;
    }
  } catch {
  }
  return 1;
};
const buildCartItem = (item, quantity) => ({
  item_name: item.product_title,
  item_id: String(item.product_id),
  item_brand: item.vendor,
  item_type: item.product_type,
  item_variant: String(item.variant_id),
  item_variant_name: item.variant_title,
  item_image: toAbsoluteUrl(item.featured_image?.url),
  item_url: toAbsoluteUrl(item.url),
  price: item.price / 100,
  sku: item.sku,
  quantity
});
const diffCart = ({ snapshot, newCart, onAddToCart, onRemoveFromCart }) => {
  const currency = activeCurrency();
  const snapshotMap = new Map(snapshot.items.map((item) => [item.key, item]));
  const newMap = new Map(newCart.items.map((item) => [item.key, item]));
  snapshotMap.forEach((oldItem, key) => {
    const newQty = newMap.get(key)?.quantity ?? 0;
    if (newQty < oldItem.quantity) {
      const qtyRemoved = oldItem.quantity - newQty;
      onRemoveFromCart({
        event: "edition_remove_from_cart",
        ecommerce: {
          timestamp: (/* @__PURE__ */ new Date()).toISOString(),
          url: window.location.href,
          currency,
          value: oldItem.price / 100 * qtyRemoved,
          items: [buildCartItem(oldItem, qtyRemoved)]
        }
      });
    }
  });
  newMap.forEach((newItem, key) => {
    const oldQty = snapshotMap.get(key)?.quantity ?? 0;
    if (newItem.quantity > oldQty) {
      const qtyAdded = newItem.quantity - oldQty;
      onAddToCart({
        event: "edition_add_to_cart",
        ecommerce: {
          timestamp: (/* @__PURE__ */ new Date()).toISOString(),
          url: window.location.href,
          currency,
          value: newItem.price / 100 * qtyAdded,
          items: [buildCartItem(newItem, qtyAdded)]
        }
      });
    }
  });
};
const initCartInterceptor = ({ onAddToCart, onRemoveFromCart }) => {
  const originalFetch = window.fetch.bind(window);
  const fetchCart = () => originalFetch("/cart.json").then((r) => r.json());
  window.fetch = async (input, init) => {
    const url = resolveUrl(input);
    if (url.includes("/cart/add")) {
      const response = await originalFetch(input, init);
      const addedQuantity = parseRequestQuantity(init);
      response.clone().json().then((data) => {
        const items = "items" in data ? data.items : [data];
        const currency = activeCurrency();
        items.forEach((item) => {
          onAddToCart({
            event: "edition_add_to_cart",
            ecommerce: {
              timestamp: (/* @__PURE__ */ new Date()).toISOString(),
              url: window.location.href,
              currency,
              value: item.price / 100 * addedQuantity,
              items: [
                {
                  item_name: item.product_title,
                  item_id: String(item.id),
                  item_brand: item.vendor ?? null,
                  item_type: item.product_type,
                  item_variant: String(item.id),
                  item_variant_name: item.variant_title,
                  item_image: item.featured_image?.url ?? null,
                  item_url: item.url,
                  price: item.price / 100,
                  sku: item.sku ?? null,
                  quantity: addedQuantity
                }
              ]
            }
          });
        });
      }).catch(() => {
      });
      return response;
    }
    if (url.includes("/cart/update") || url.includes("/cart/change")) {
      const snapshot = await fetchCart();
      const response = await originalFetch(input, init);
      const newCart = await fetchCart();
      diffCart({ snapshot, newCart, onAddToCart, onRemoveFromCart });
      return response;
    }
    return originalFetch(input, init);
  };
};
const DEBUG_MODE = true;
const CONSENT_STORAGE_KEY = "cookieSelections";
const DL_QUEUE = { KEY: "dl_pending_queue", TTL_MINUTES: 30, MAX_SIZE: 20 };
const PAGE_DATA_DEFAULTS = {
  pageType: "other",
  product: null,
  collection: null,
  searchQuery: null,
  customer: null
};
class DataLayer {
  /**
   * Registers the two window event listeners that drive the lifecycle.
   * Must be called once from `app.ts` — safe to call before `CookieBanner` connects.
   *
   * - `edition:consent-default-set` → runs full initialisation (once).
   * - `edition:consent-updated`     → flushes the session queue.
   */
  init = () => {
    window.addEventListener(
      "edition:consent-default-set",
      () => {
        this.initialize().catch((err) => logger.error("DataLayer init failed", err));
      },
      { once: true }
    );
    window.addEventListener("edition:consent-updated", () => {
      this.flushQueue();
    });
  };
  /** Runs the full initialisation sequence — pushes context, user, and page events then wires up interceptors. */
  initialize = async () => {
    window.dataLayer = window.dataLayer || [];
    const pageData = this.readPageData();
    this.pushLocation();
    this.pushContextData({ pageType: pageData.pageType });
    this.pushDeviceData();
    if (pageData.customer) {
      await this.pushUserData({ customer: pageData.customer });
    }
    await this.pushPageTypeEvent({ pageData });
    initCartInterceptor({
      onAddToCart: (payload) => this.pushToDataLayer({ payload, consentRequirement: "analytics" }),
      onRemoveFromCart: (payload) => this.pushToDataLayer({ payload, consentRequirement: "analytics" })
    });
    this.bindFormListeners();
    window.editionDataLayer = {
      trackCartView: this.trackCartView,
      trackAddToCart: this.trackAddToCart,
      trackRemoveFromCart: this.trackRemoveFromCart
    };
    {
      debugLog({ category: "init", label: "DataLayer initialized", payload: { pageType: pageData.pageType } });
    }
  };
  /** Parses the `#datalayer-data` JSON script tag injected by `datalayer-data.liquid`. Returns safe defaults on failure. */
  readPageData = () => {
    const el = document.getElementById("datalayer-data");
    if (!el?.textContent) {
      return PAGE_DATA_DEFAULTS;
    }
    try {
      return JSON.parse(el.textContent);
    } catch {
      logger.warn("Failed to parse #datalayer-data JSON");
      return PAGE_DATA_DEFAULTS;
    }
  };
  /**
   * Pushes a payload to `window.dataLayer` if the required consent is granted,
   * otherwise holds it in `sessionStorage` for later flushing.
   */
  pushToDataLayer = ({ payload, consentRequirement }) => {
    const isEvent = payload.event.startsWith("edition_");
    if (this.checkConsent({ requirement: consentRequirement })) {
      window.dataLayer.push(payload);
      {
        debugLog({ category: isEvent ? "event" : "context", label: payload.event, payload });
      }
    } else {
      this.queueEvent({ payload, consentRequirement });
      {
        debugLog({ category: "config", label: `Queued (no consent): ${payload.event}`, payload });
      }
    }
  };
  /** Reads consent state from localStorage — same key written by `CookieBanner`. */
  checkConsent = ({ requirement }) => {
    if (requirement === "none") {
      return true;
    }
    const selections = local.get(CONSENT_STORAGE_KEY);
    if (!selections) {
      return false;
    }
    if (requirement === "analytics") {
      return selections.analytics;
    }
    if (requirement === "marketing") {
      return selections.marketing;
    }
    return false;
  };
  /** Appends an event to the queue, dropping the oldest if the cap is reached. Persists for 30 minutes across page loads. */
  queueEvent = (event) => {
    const queue = this.getQueue();
    if (queue.length >= DL_QUEUE.MAX_SIZE) {
      queue.shift();
    }
    queue.push(event);
    local.set(DL_QUEUE.KEY, queue, DL_QUEUE.TTL_MINUTES);
  };
  /** Returns the current queue from localStorage, or an empty array if nothing is queued or TTL has expired. */
  getQueue = () => local.get(DL_QUEUE.KEY) ?? [];
  /**
   * Replays all queued events that now have consent, leaving the rest in place.
   * Called automatically on `edition:consent-updated`.
   */
  flushQueue = () => {
    const queue = this.getQueue();
    if (!queue.length) {
      return;
    }
    const remaining = [];
    queue.forEach(({ payload, consentRequirement }) => {
      if (this.checkConsent({ requirement: consentRequirement })) {
        window.dataLayer.push(payload);
        {
          debugLog({ category: "event", label: `Flushed: ${payload.event}`, payload });
        }
      } else {
        remaining.push({ payload, consentRequirement });
      }
    });
    local.set(DL_QUEUE.KEY, remaining, DL_QUEUE.TTL_MINUTES);
    const flushed = queue.length - remaining.length;
    if (flushed > 0 && DEBUG_MODE) {
      debugLog({ category: "init", label: `Flushed ${flushed} queued events`, payload: { remaining: remaining.length } });
    }
  };
  /** Pushes the current page URL breakdown and shop domain. No consent required. */
  pushLocation = () => {
    const { hash, host, hostname, href, origin, pathname, port, protocol, search } = window.location;
    this.pushToDataLayer({
      payload: {
        event: "location",
        location: { hash, host, hostname, href, origin, pathname, port, protocol, search },
        shop: window.Shopify?.shop ?? null
      },
      consentRequirement: "none"
    });
  };
  /** Pushes page type, title, and URL. No consent required. */
  pushContextData = ({ pageType }) => {
    this.pushToDataLayer({
      payload: {
        event: "contextData",
        page: { type: pageType, title: document.title, url: window.location.href }
      },
      consentRequirement: "none"
    });
  };
  /** Pushes browser/device signals — language, screen size, user agent, cookie state. No consent required. */
  pushDeviceData = () => {
    this.pushToDataLayer({
      payload: {
        event: "deviceData",
        encoding: document.characterSet,
        language: navigator.language,
        cookieEnabled: navigator.cookieEnabled,
        screen: `${window.innerWidth}x${window.innerHeight}`,
        userAgent: navigator.userAgent
      },
      consentRequirement: "none"
    });
  };
  /** Hashes PII fields with SHA-256 before pushing — email, phone, first/last name. */
  pushUserData = async ({ customer }) => {
    const [sha256Email, sha256Phone, sha256FirstName, sha256LastName] = await Promise.all([
      customer.email ? sha256(customer.email.toLowerCase().trim()) : null,
      customer.phone ? sha256(customer.phone.replace(/\D/g, "")) : null,
      customer.first_name ? sha256(customer.first_name.toLowerCase().trim()) : null,
      customer.last_name ? sha256(customer.last_name.toLowerCase().trim()) : null
    ]);
    this.pushToDataLayer({
      payload: {
        event: "userData",
        customer: {
          email: customer.email ?? null,
          phone_number: customer.phone ?? null,
          sha256_email_address: sha256Email,
          sha256_phone_number: sha256Phone,
          address: customer.first_name ?? customer.last_name ? {
            first_name: customer.first_name ?? null,
            last_name: customer.last_name ?? null,
            sha256_first_name: sha256FirstName,
            sha256_last_name: sha256LastName
          } : null
        }
      },
      consentRequirement: "analytics"
    });
  };
  /**
   * Dispatches the page-type-specific event based on `pageData.pageType`.
   * For `search`, reads `?q` from the URL rather than relying on Liquid output.
   */
  pushPageTypeEvent = async ({ pageData }) => {
    switch (pageData.pageType) {
      case "product": {
        if (pageData.product) {
          this.pushViewItem({ product: pageData.product });
        }
        break;
      }
      case "collection": {
        if (pageData.collection) {
          this.pushCollectionView({ collection: pageData.collection });
        }
        break;
      }
      case "search": {
        const query = new URLSearchParams(window.location.search).get("q") ?? pageData.searchQuery;
        if (query) {
          this.pushSearch({ query });
        }
        break;
      }
      case "cart": {
        await this.trackCartView();
        break;
      }
      case "other": {
        break;
      }
      default: {
        const _exhaustive = pageData.pageType;
        logger.warn("Unhandled DataLayer page type", _exhaustive);
      }
    }
  };
  /** Pushes `edition_view_item` using the URL-selected variant, falling back to the first variant. Requires analytics consent. */
  pushViewItem = ({ product }) => {
    const urlVariantId = new URLSearchParams(window.location.search).get("variant");
    const variant = (urlVariantId ? product.variants.find((v) => String(v.id) === urlVariantId) : null) ?? product.variants[0];
    if (!variant) {
      return;
    }
    this.pushToDataLayer({
      payload: {
        event: "edition_view_item",
        ecommerce: {
          timestamp: (/* @__PURE__ */ new Date()).toISOString(),
          url: window.location.href,
          currency: window.Shopify?.currency?.active ?? "",
          value: variant.price / 100,
          items: [
            {
              item_name: product.title,
              item_id: String(product.id),
              item_brand: product.vendor,
              item_type: product.type,
              item_variant: String(variant.id),
              item_variant_name: variant.title,
              item_image: toAbsoluteUrl(variant.featured_image?.src ?? product.featured_image),
              item_url: toAbsoluteUrl(`/products/${product.handle}`),
              price: variant.price / 100,
              sku: variant.sku ?? null,
              quantity: 1
            }
          ]
        }
      },
      consentRequirement: "analytics"
    });
  };
  /** Pushes `edition_collection_view` with all currently-rendered products (first 30 from Liquid). Requires analytics consent. */
  pushCollectionView = ({ collection }) => {
    const { title, handle, products } = collection;
    this.pushToDataLayer({
      payload: {
        event: "edition_collection_view",
        ecommerce: {
          timestamp: (/* @__PURE__ */ new Date()).toISOString(),
          url: window.location.href,
          currency: window.Shopify?.currency?.active ?? "",
          value: 1,
          item_list_name: title,
          item_list_id: handle,
          item_list_products_count: products.length,
          items: products.flatMap(
            (p) => p.variants.map(
              (v) => ({
                item_name: p.title,
                item_id: String(p.id),
                item_brand: p.vendor,
                item_type: p.type,
                item_variant: String(v.id),
                item_variant_name: v.title,
                item_image: toAbsoluteUrl(v.featured_image?.src ?? p.featured_image),
                item_url: toAbsoluteUrl(`/products/${p.handle}`),
                price: v.price / 100,
                sku: v.sku ?? null
              })
            )
          )
        }
      },
      consentRequirement: "analytics"
    });
  };
  /** Pushes `edition_search` with the query string only — no result products. Requires analytics consent. */
  pushSearch = ({ query }) => {
    this.pushToDataLayer({
      payload: {
        event: "edition_search",
        search_term: query,
        ecommerce: { timestamp: (/* @__PURE__ */ new Date()).toISOString(), url: window.location.href }
      },
      consentRequirement: "analytics"
    });
  };
  /** Fetches `/cart.json` and pushes `edition_cart_view`. Call this when a cart drawer opens. */
  trackCartView = async () => {
    try {
      const response = await fetch("/cart.json");
      const cart = await response.json();
      this.pushToDataLayer({
        payload: {
          event: "edition_cart_view",
          ecommerce: {
            timestamp: (/* @__PURE__ */ new Date()).toISOString(),
            url: window.location.href,
            currency: cart.currency,
            value: cart.total_price / 100,
            items: cart.items.map(
              (item) => ({
                item_name: item.product_title,
                item_id: String(item.product_id),
                item_brand: item.vendor,
                item_type: item.product_type,
                item_variant: String(item.variant_id),
                item_variant_name: item.variant_title,
                item_image: toAbsoluteUrl(item.featured_image?.url),
                item_url: toAbsoluteUrl(item.url),
                price: item.price / 100,
                sku: item.sku,
                quantity: item.quantity
              })
            )
          }
        },
        consentRequirement: "analytics"
      });
    } catch (err) {
      logger.error("DataLayer: failed to fetch cart", err);
    }
  };
  /** Manually push an `edition_add_to_cart` event — use when a custom flow bypasses `/cart/add.js`. */
  trackAddToCart = (payload) => {
    this.pushToDataLayer({ payload, consentRequirement: "analytics" });
  };
  /** Manually push an `edition_remove_from_cart` event — use when a custom flow bypasses `/cart/update.js`. */
  trackRemoveFromCart = (payload) => {
    this.pushToDataLayer({ payload, consentRequirement: "analytics" });
  };
  /** Listens for contact form submits on `document` and distinguishes newsletter vs contact events by form fields. */
  bindFormListeners = () => {
    document.addEventListener("submit", (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) {
        return;
      }
      const action = form.getAttribute("action") ?? "";
      if (!action.includes("/contact")) {
        return;
      }
      const elements = Array.from(form.elements).filter((el) => el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement);
      const getField = (name) => elements.find((el) => el.name === name)?.value ?? null;
      const isNewsletter = elements.some((el) => el.name === "contact[tags]" && el instanceof HTMLInputElement && el.value === "newsletter");
      if (isNewsletter) {
        this.pushToDataLayer({
          payload: {
            event: "edition_newsletter_subscribe",
            timestamp: (/* @__PURE__ */ new Date()).toISOString(),
            customer_email: getField("contact[email]")
          },
          consentRequirement: "analytics"
        });
      } else {
        this.pushToDataLayer({
          payload: {
            event: "edition_contact_form_submit",
            timestamp: (/* @__PURE__ */ new Date()).toISOString(),
            customer_name: getField("contact[Name]"),
            customer_email: getField("contact[email]"),
            customer_phone: getField("contact[Phone number]"),
            comment: getField("contact[Comment]")
          },
          consentRequirement: "analytics"
        });
      }
    });
  };
}
let leafletPromise = null;
let clusterPromise = null;
const loadStylesheet = (href) => new Promise((resolve, reject) => {
  if (document.querySelector("link[data-leaflet-css]")) {
    resolve();
    return;
  }
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  link.dataset.leafletCss = "";
  link.addEventListener("load", () => resolve(), { once: true });
  link.addEventListener("error", () => reject(new Error(`Could not load ${href}`)), { once: true });
  document.head.appendChild(link);
});
const loadScript = (src) => new Promise((resolve, reject) => {
  if (window.L) {
    resolve();
    return;
  }
  const existing = document.querySelector("script[data-leaflet-js]");
  if (existing) {
    existing.addEventListener("load", () => resolve(), { once: true });
    existing.addEventListener("error", () => reject(new Error(`Could not load ${src}`)), { once: true });
    return;
  }
  const script = document.createElement("script");
  script.src = src;
  script.defer = true;
  script.dataset.leafletJs = "";
  script.addEventListener("load", () => resolve(), { once: true });
  script.addEventListener("error", () => reject(new Error(`Could not load ${src}`)), { once: true });
  document.head.appendChild(script);
});
const loadLeaflet = async ({ scriptUrl, styleUrl }) => {
  if (leafletPromise) {
    return leafletPromise;
  }
  leafletPromise = (async () => {
    await Promise.all([loadScript(scriptUrl), loadStylesheet(styleUrl)]);
    if (!window.L) {
      throw new Error("Leaflet loaded but window.L is undefined");
    }
    return window.L;
  })();
  try {
    return await leafletPromise;
  } catch (error) {
    leafletPromise = null;
    logger.error("DealerMap: failed to load Leaflet", { error });
    throw error;
  }
};
const injectOnce = ({ tag, url, marker }) => new Promise((resolve, reject) => {
  const existing = document.querySelector(`${tag}[data-${marker}]`);
  if (existing) {
    existing.addEventListener("load", () => resolve(), { once: true });
    existing.addEventListener("error", () => reject(new Error(`Could not load ${url}`)), { once: true });
    return;
  }
  const element = document.createElement(tag);
  if (element instanceof HTMLScriptElement) {
    element.src = url;
    element.defer = true;
  } else if (element instanceof HTMLLinkElement) {
    element.rel = "stylesheet";
    element.href = url;
  }
  element.setAttribute(`data-${marker}`, "");
  element.addEventListener("load", () => resolve(), { once: true });
  element.addEventListener("error", () => reject(new Error(`Could not load ${url}`)), { once: true });
  document.head.appendChild(element);
});
const loadMarkerCluster = async ({ scriptUrl, styleUrl }) => {
  if (clusterPromise) {
    return clusterPromise;
  }
  if (!leafletPromise) {
    throw new Error("loadMarkerCluster called before loadLeaflet");
  }
  clusterPromise = (async () => {
    await leafletPromise;
    await Promise.all([injectOnce({ tag: "script", url: scriptUrl, marker: "cluster-js" }), injectOnce({ tag: "link", url: styleUrl, marker: "cluster-css" })]);
    if (typeof window.L?.markerClusterGroup !== "function") {
      throw new Error("markercluster loaded but L.markerClusterGroup is undefined");
    }
  })();
  try {
    return await clusterPromise;
  } catch (error) {
    clusterPromise = null;
    logger.error("DealerMap: failed to load Leaflet.markercluster", { error });
    throw error;
  }
};
const PIN_SVG = `
<svg width="28" height="38" viewBox="0 0 28 38" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
  <path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 24 14 24s14-13.5 14-24c0-7.732-6.268-14-14-14Z" fill="currentColor"/>
  <circle cx="14" cy="14" r="5" fill="#fff"/>
</svg>`;
const createPinIcon = ({ variant = "dealer" } = {}) => L.divIcon({
  html: PIN_SVG,
  className: variant === "search" ? "dealer-map__pin dealer-map__pin--search" : "dealer-map__pin",
  iconSize: [28, 38],
  iconAnchor: [14, 38],
  popupAnchor: [0, -38],
  tooltipAnchor: [0, -38]
});
const createClusterIcon = (cluster) => {
  const label = document.createElement("span");
  label.textContent = String(cluster.getChildCount());
  return L.divIcon({
    html: label.outerHTML,
    className: "dealer-map__cluster",
    iconSize: [36, 36],
    iconAnchor: [18, 18]
  });
};
const formatAddress = (dealer) => {
  const street = dealer.street?.trim() ?? "";
  if (!street) {
    return "";
  }
  const area = [dealer.postalCode, dealer.city].map((part) => part?.trim() ?? "").filter(Boolean).join(" ");
  return area ? `${street}, ${area}` : street;
};
const directionsDestination = (dealer) => {
  const address = formatAddress(dealer);
  if (address) {
    return address;
  }
  return `${dealer.latitude},${dealer.longitude}`;
};
const buildDirectionsUrl = (dealer) => {
  const destination = encodeURIComponent(directionsDestination(dealer)).replace(/%20/g, "+").replace(/%2C/g, ",");
  return `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
};
const CACHE_TTL_MINUTES = 60 * 24 * 90;
const NEGATIVE_TTL_MINUTES = 60 * 24 * 7;
const MIN_REQUEST_INTERVAL_MS = 1100;
const MAX_LOOKUPS_PER_PAGELOAD = 25;
const NOMINATIM_ENDPOINT = "https://nominatim.openstreetmap.org/search";
const cache = local.namespace("dealer-geo");
const queryCache = local.namespace("dealer-geo-query");
let lastRequestAt = 0;
let lookupsThisPageLoad = 0;
const wait = (ms) => new Promise((resolve) => {
  window.setTimeout(resolve, ms);
});
const geocodableStreet = (street) => (street ?? "").split(",")[0].trim();
const cacheKey = (dealer) => [geocodableStreet(dealer.street), dealer.postalCode, dealer.city, dealer.countryCode].map((part) => (part ?? "").trim().toLowerCase().replace(/\s+/g, " ")).join("|");
const buildUrl = (dealer) => {
  const params = new URLSearchParams({
    street: geocodableStreet(dealer.street),
    postalcode: dealer.postalCode,
    city: dealer.city,
    country: dealer.countryCode,
    format: "jsonv2",
    limit: "1",
    addressdetails: "0"
  });
  return `${NOMINATIM_ENDPOINT}?${params.toString()}`;
};
const fetchJson = async (url, signal) => {
  const elapsed = Date.now() - lastRequestAt;
  if (elapsed < MIN_REQUEST_INTERVAL_MS) {
    await wait(MIN_REQUEST_INTERVAL_MS - elapsed);
  }
  lastRequestAt = Date.now();
  const response = await fetch(url, {
    signal,
    headers: { Accept: "application/json" }
  });
  if (!response.ok) {
    throw new Error(`Nominatim responded ${response.status}`);
  }
  const results = await response.json();
  const first = results[0];
  if (!first) {
    return null;
  }
  const lat = Number.parseFloat(first.lat);
  const lng = Number.parseFloat(first.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  return { lat, lng };
};
const fetchFromNominatim = async (dealer, signal) => fetchJson(buildUrl(dealer), signal);
const geocodeQuery = async ({ query, signal }) => {
  const trimmed = query.trim();
  if (!trimmed) {
    return null;
  }
  const key = trimmed.toLowerCase().replace(/\s+/g, " ");
  const cached = queryCache.get(key);
  if (cached) {
    return cached.ok ? { lat: cached.lat, lng: cached.lng } : null;
  }
  const params = new URLSearchParams({
    q: trimmed,
    countrycodes: "dk,se,no,de",
    format: "jsonv2",
    limit: "1",
    addressdetails: "0"
  });
  const point = await fetchJson(`${NOMINATIM_ENDPOINT}?${params.toString()}`, signal);
  if (local.available) {
    if (point) {
      queryCache.set(key, { ok: true, ...point }, CACHE_TTL_MINUTES);
    } else {
      queryCache.set(key, { ok: false }, NEGATIVE_TTL_MINUTES);
    }
  }
  return point;
};
const resolveDealer = async (dealer, signal) => {
  if (Number.isFinite(dealer.latitude) && Number.isFinite(dealer.longitude)) {
    return { lat: dealer.latitude, lng: dealer.longitude };
  }
  if (!dealer.street?.trim()) {
    return null;
  }
  if (!local.available) {
    logger.warn("DealerMap: localStorage unavailable, skipping geocoding", { dealer: dealer.name });
    return null;
  }
  const key = cacheKey(dealer);
  const cached = cache.get(key);
  if (cached) {
    return cached.ok ? { lat: cached.lat, lng: cached.lng } : null;
  }
  if (lookupsThisPageLoad >= MAX_LOOKUPS_PER_PAGELOAD) {
    logger.warn("DealerMap: geocoding budget for this page load exhausted", { dealer: dealer.name });
    return null;
  }
  lookupsThisPageLoad += 1;
  try {
    const point = await fetchFromNominatim(dealer, signal);
    if (!point) {
      cache.set(key, { ok: false }, NEGATIVE_TTL_MINUTES);
      logger.warn("DealerMap: address not found by Nominatim", { dealer: dealer.name, key });
      return null;
    }
    cache.set(key, { ok: true, ...point }, CACHE_TTL_MINUTES);
    return point;
  } catch (error) {
    if (error.name !== "AbortError") {
      logger.error("DealerMap: geocoding request failed", { dealer: dealer.name, error });
    }
    return null;
  }
};
const initTabs = ({ root, defaultTab, onChange }) => {
  const buttons = Array.from(root.querySelectorAll("[data-dealer-tab]"));
  const panels = Array.from(root.querySelectorAll("[data-dealer-panel]"));
  let active = defaultTab;
  const applyState = () => {
    for (const button of buttons) {
      const isActive = button.dataset.dealerTab === active;
      button.setAttribute("aria-selected", String(isActive));
      button.tabIndex = isActive ? 0 : -1;
      button.classList.toggle("dealer-map__tab--active", isActive);
    }
    for (const panel of panels) {
      panel.hidden = panel.dataset.dealerPanel !== active;
    }
  };
  const activate = (tab) => {
    const exists = buttons.some((button) => button.dataset.dealerTab === tab);
    if (!exists) {
      return;
    }
    active = tab;
    applyState();
    onChange(tab);
  };
  const handleClick = (event) => {
    const button = event.target.closest("[data-dealer-tab]");
    if (!button) {
      return;
    }
    activate(button.dataset.dealerTab);
  };
  const handleKeydown = (event) => {
    const isArrow = event.key === "ArrowLeft" || event.key === "ArrowRight";
    if (!isArrow || !event.target.closest("[data-dealer-tab]")) {
      return;
    }
    event.preventDefault();
    const currentIndex = buttons.findIndex((button) => button.dataset.dealerTab === active);
    const direction = event.key === "ArrowRight" ? 1 : -1;
    const nextIndex = (currentIndex + direction + buttons.length) % buttons.length;
    const next = buttons[nextIndex];
    next.focus();
    activate(next.dataset.dealerTab);
  };
  root.addEventListener("click", handleClick);
  root.addEventListener("keydown", handleKeydown);
  if (!buttons.some((button) => button.dataset.dealerTab === active) && buttons.length) {
    active = buttons[0].dataset.dealerTab;
  }
  applyState();
  onChange(active);
  return {
    activate,
    current: () => active,
    destroy: () => {
      root.removeEventListener("click", handleClick);
      root.removeEventListener("keydown", handleKeydown);
    }
  };
};
const EARTH_RADIUS_KM = 6371;
const toRadians = (degrees) => degrees * Math.PI / 180;
const haversineKm = ({ from, to }) => {
  const dLat = toRadians(to.lat - from.lat);
  const dLng = toRadians(to.lng - from.lng);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(a));
};
const formatDistanceKm = ({ km, locale }) => {
  const formatter = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  return `${formatter.format(km)} km`;
};
const NEAREST_FALLBACK_COUNT = 5;
const collectRows = ({ container }) => Array.from(container.querySelectorAll("[data-dealer-id]")).map((element) => ({
  id: element.dataset.dealerId ?? "",
  name: (element.dataset.dealerName ?? "").toLowerCase(),
  element,
  distanceEl: element.querySelector("[data-dealer-distance]")
}));
const sortAlphabetically = ({ container, rows }) => {
  const sorted = [...rows].sort((a, b) => a.name.localeCompare(b.name, "da"));
  for (const row of sorted) {
    container.appendChild(row.element);
  }
};
const applyDistances = ({ container, rows, points, origin, radiusKm, locale }) => {
  const measured = rows.map((row) => {
    const point = points.get(row.id);
    return point ? { row, km: haversineKm({ from: origin, to: point }) } : { row, km: null };
  }).filter((entry) => entry.km !== null).sort((a, b) => a.km - b.km);
  const inRadius = measured.filter((entry) => entry.km <= radiusKm);
  const usedNearestFallback = inRadius.length === 0 && measured.length > 0;
  const visible = usedNearestFallback ? measured.slice(0, NEAREST_FALLBACK_COUNT) : inRadius;
  const visibleIds = new Set(visible.map((entry) => entry.row.id));
  for (const row of rows) {
    row.element.hidden = !visibleIds.has(row.id);
  }
  for (const { row, km } of visible) {
    if (row.distanceEl) {
      row.distanceEl.textContent = formatDistanceKm({ km, locale });
      row.distanceEl.hidden = false;
    }
    container.appendChild(row.element);
  }
  return { matched: visible.map((entry) => entry.row.id), usedNearestFallback };
};
const resetDistances = ({ container, rows }) => {
  for (const row of rows) {
    row.element.hidden = false;
    if (row.distanceEl) {
      row.distanceEl.hidden = true;
      row.distanceEl.textContent = "";
    }
  }
  sortAlphabetically({ container, rows });
};
const filterByName = ({ rows, query }) => {
  const needle = query.trim().toLowerCase();
  for (const row of rows) {
    row.element.hidden = needle !== "" && !row.name.includes(needle);
  }
};
const CARET_LEFT = '<svg class="icon icon--extra-small" aria-hidden="true" focusable="false" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.5 16.25L6.25 10L12.5 3.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const CARET_RIGHT = '<svg class="icon icon--extra-small" aria-hidden="true" focusable="false" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.5 3.75L13.75 10L7.5 16.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const condensePages = ({ page, totalPages }) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  const wanted = /* @__PURE__ */ new Set([1, totalPages, page - 1, page, page + 1]);
  const pages = [];
  let previous = 0;
  for (let candidate = 1; candidate <= totalPages; candidate += 1) {
    if (!wanted.has(candidate)) {
      continue;
    }
    if (candidate - previous > 1) {
      pages.push(null);
    }
    pages.push(candidate);
    previous = candidate;
  }
  return pages;
};
const renderPager = ({ nav, page, totalPages, strings, onNavigate }) => {
  nav.textContent = "";
  nav.hidden = totalPages <= 1;
  if (totalPages <= 1) {
    return;
  }
  const list = document.createElement("ul");
  list.className = "pagination__list list-unstyled flex items-center flex-wrap justify-center";
  list.setAttribute("role", "list");
  const addItem = (element) => {
    const item = document.createElement("li");
    item.append(element);
    list.append(item);
  };
  const buildButton = ({ label, targetPage }) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "pagination__item";
    button.setAttribute("aria-label", label);
    button.addEventListener("click", () => onNavigate(targetPage));
    return button;
  };
  if (page > 1) {
    const previous = buildButton({ label: strings.previous, targetPage: page - 1 });
    previous.classList.add("pagination__item--previous", "pagination__item-arrow", "motion-reduce");
    previous.innerHTML = CARET_LEFT;
    previous.append(Object.assign(document.createElement("span"), { textContent: strings.previous }));
    addItem(previous);
  }
  for (const candidate of condensePages({ page, totalPages })) {
    if (candidate === null) {
      const gap = document.createElement("span");
      gap.className = "pagination__item";
      gap.setAttribute("aria-hidden", "true");
      gap.textContent = "…";
      addItem(gap);
      continue;
    }
    const button = buildButton({ label: strings.page.replace("[page]", String(candidate)), targetPage: candidate });
    button.textContent = String(candidate);
    if (candidate === page) {
      button.classList.add("pagination__item--current", "background-2");
      button.setAttribute("aria-current", "page");
      button.disabled = true;
    }
    addItem(button);
  }
  if (page < totalPages) {
    const next = buildButton({ label: strings.next, targetPage: page + 1 });
    next.classList.add("pagination__item--next", "pagination__item-arrow", "motion-reduce");
    next.append(Object.assign(document.createElement("span"), { textContent: strings.next }));
    next.insertAdjacentHTML("beforeend", CARET_RIGHT);
    addItem(next);
  }
  nav.append(list);
};
const FALLBACK_CENTER = { lat: 56.2639, lng: 9.5018 };
const FALLBACK_ZOOM = 7;
const DEFAULT_RADIUS_KM = 25;
const DEFAULT_LIST_PAGE_SIZE = 9;
const DEFAULT_TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const DEFAULT_TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const DEFAULT_STRINGS = {
  resultsHeading: "[count] forhandlere nær [query]",
  resultsHeadingAll: "Alle forhandlere",
  noResults: "Ingen forhandlere inden for [radius] km — viser de [count] nærmeste",
  searchPlaceholderGeo: "Postnummer eller by",
  searchPlaceholderWebshop: "Søg efter webshop",
  searchError: "Stedet blev ikke fundet. Prøv et postnummer eller bynavn.",
  paginationPrevious: "Forrige",
  paginationNext: "Næste",
  paginationPage: "Side [page]"
};
const readBool = (value, fallback) => {
  if (value === void 0 || value === "") {
    return fallback;
  }
  return value === "true";
};
const toCoordinate = (value) => {
  const parsed = typeof value === "string" ? Number.parseFloat(value) : value;
  return typeof parsed === "number" && Number.isFinite(parsed) ? parsed : void 0;
};
const readNumber = (value, fallback) => {
  const parsed = Number.parseFloat(value ?? "");
  return Number.isFinite(parsed) ? parsed : fallback;
};
const readTab = (value) => value === "list" || value === "webshop" ? value : "map";
const alphabeticalIds = (rows) => [...rows].sort((a, b) => a.name.localeCompare(b.name, "da")).map((row) => row.id);
class DealerMap extends HTMLElement {
  canvas = null;
  map = null;
  dealers = [];
  settings = null;
  strings = DEFAULT_STRINGS;
  observer = null;
  resizeObserver = null;
  abortController = new AbortController();
  prefersReducedMotion = false;
  hasInitialised = false;
  clusterGroup = null;
  markersById = /* @__PURE__ */ new Map();
  resolvedPoints = /* @__PURE__ */ new Map();
  searchMarker = null;
  searchState = { point: null, label: "" };
  webshopQuery = "";
  listPage = 1;
  listOrder = [];
  pagerNav = null;
  tabs = null;
  sidebar = null;
  list = null;
  webshop = null;
  headingEl = null;
  noticeEl = null;
  loadingEl = null;
  searchInput = null;
  cleanups = [];
  connectedCallback() {
    this.abortController = new AbortController();
    this.hasInitialised = false;
    this.canvas = this.querySelector("[data-dealer-map-canvas]");
    this.settings = this.readSettings();
    this.strings = this.readStrings();
    this.dealers = this.readDealers();
    this.prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!this.dealers.length && this.canvas) {
      logger.warn("DealerMap: no placeable dealers in the section payload");
    }
    if (window.Shopify?.designMode) {
      void this.init();
      return;
    }
    this.observer = new IntersectionObserver(this.handleIntersect, { rootMargin: "300px" });
    this.observer.observe(this);
  }
  disconnectedCallback() {
    this.abortController.abort();
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    this.tabs?.destroy();
    this.tabs = null;
    for (const cleanup of this.cleanups) {
      cleanup();
    }
    this.cleanups = [];
    this.map?.remove();
    this.map = null;
    this.clusterGroup = null;
    this.searchMarker = null;
    this.markersById.clear();
    this.resolvedPoints.clear();
  }
  handleIntersect = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) {
      return;
    }
    this.observer?.disconnect();
    this.observer = null;
    void this.init();
  };
  readSettings = () => ({
    leafletJs: this.dataset.leafletJs ?? "",
    leafletCss: this.dataset.leafletCss ?? "",
    clusterJs: this.dataset.clusterJs ?? "",
    clusterCss: this.dataset.clusterCss ?? "",
    tileUrl: this.dataset.tileUrl || DEFAULT_TILE_URL,
    tileSubdomains: this.dataset.tileSubdomains ?? "",
    tileAttribution: this.dataset.tileAttribution || DEFAULT_TILE_ATTRIBUTION,
    initialLat: readNumber(this.dataset.initialLat, FALLBACK_CENTER.lat),
    initialLng: readNumber(this.dataset.initialLng, FALLBACK_CENTER.lng),
    initialZoom: readNumber(this.dataset.initialZoom, FALLBACK_ZOOM),
    maxZoomOnFit: readNumber(this.dataset.maxZoomOnFit, 13),
    fitBounds: readBool(this.dataset.fitBounds, true),
    scrollWheelZoom: readBool(this.dataset.scrollWheelZoom, false),
    showPhone: readBool(this.dataset.showPhone, true),
    showWebsite: readBool(this.dataset.showWebsite, true),
    showEmail: readBool(this.dataset.showEmail, false),
    showOpeningHours: readBool(this.dataset.showOpeningHours, false),
    directionsStyle: this.dataset.directionsStyle ?? "btn--primary",
    directionsLabel: this.dataset.directionsLabel ?? "Rutevejledning",
    radiusKm: readNumber(this.dataset.radiusKm, DEFAULT_RADIUS_KM),
    defaultTab: readTab(this.dataset.defaultTab),
    listPageSize: readNumber(this.dataset.listPageSize, DEFAULT_LIST_PAGE_SIZE)
  });
  readStrings = () => {
    const script = this.querySelector("[data-dealer-map-i18n]");
    if (!script?.textContent) {
      return DEFAULT_STRINGS;
    }
    try {
      return { ...DEFAULT_STRINGS, ...JSON.parse(script.textContent) };
    } catch (error) {
      logger.error("DealerMap: could not parse the i18n payload", { error });
      return DEFAULT_STRINGS;
    }
  };
  readDealers = () => {
    const script = this.querySelector("[data-dealer-map-data]");
    if (!script?.textContent) {
      logger.warn("DealerMap: missing [data-dealer-map-data] payload");
      return [];
    }
    try {
      const parsed = JSON.parse(script.textContent);
      if (!Array.isArray(parsed)) {
        return [];
      }
      return parsed.map((dealer) => ({
        ...dealer,
        latitude: toCoordinate(dealer.latitude),
        longitude: toCoordinate(dealer.longitude)
      }));
    } catch (error) {
      logger.error("DealerMap: could not parse the dealer payload", { error });
      return [];
    }
  };
  init = async () => {
    if (this.hasInitialised || !this.settings) {
      return;
    }
    this.hasInitialised = true;
    this.setupViews();
    if (!this.canvas) {
      if (this.dealers.length) {
        logger.warn("DealerMap: dealers in the payload but no [data-dealer-map-canvas] to draw them on");
      }
      return;
    }
    this.loadingEl = this.querySelector("[data-dealer-map-loading]");
    try {
      await loadLeaflet({ scriptUrl: this.settings.leafletJs, styleUrl: this.settings.leafletCss });
    } catch {
      this.showLoadingError();
      return;
    }
    try {
      await loadMarkerCluster({ scriptUrl: this.settings.clusterJs, styleUrl: this.settings.clusterCss });
    } catch {
    }
    if (this.abortController.signal.aborted) {
      return;
    }
    this.createMap();
    await this.addMarkers();
  };
  setupViews = () => {
    this.headingEl = this.querySelector("[data-dealer-results-heading]");
    this.noticeEl = this.querySelector("[data-dealer-no-results]");
    this.sidebar = this.collectRowCollection("[data-dealer-sidebar-list]");
    this.list = this.collectRowCollection("[data-dealer-list]");
    this.webshop = this.collectRowCollection("[data-dealer-webshops]");
    for (const collection of [this.sidebar, this.list, this.webshop]) {
      if (collection) {
        sortAlphabetically(collection);
      }
    }
    this.pagerNav = this.querySelector("[data-dealer-list-pagination]");
    this.listPage = 1;
    this.listOrder = this.list ? alphabeticalIds(this.list.rows) : [];
    this.renderListPage();
    this.setupSearch();
    this.setupSidebarClicks();
    this.tabs = initTabs({
      root: this,
      defaultTab: this.settings?.defaultTab ?? "map",
      onChange: this.handleTabChange
    });
  };
  collectRowCollection = (selector) => {
    const container = this.querySelector(selector);
    return container ? { container, rows: collectRows({ container }) } : null;
  };
  setupSearch = () => {
    const form = this.querySelector("[data-dealer-search]");
    this.searchInput = this.querySelector("[data-dealer-search-input]");
    if (!form || !this.searchInput) {
      return;
    }
    const handleSubmit = (event) => {
      event.preventDefault();
      if (this.tabs?.current() === "webshop") {
        return;
      }
      const query = this.searchInput?.value.trim() ?? "";
      if (!query) {
        this.clearSearch();
        return;
      }
      void this.runGeoSearch(query);
    };
    const handleInput = debounce(() => {
      if (this.tabs?.current() !== "webshop" || !this.webshop) {
        return;
      }
      this.webshopQuery = this.searchInput?.value ?? "";
      filterByName({ rows: this.webshop.rows, query: this.webshopQuery });
    }, 150);
    form.addEventListener("submit", handleSubmit);
    this.searchInput.addEventListener("input", handleInput);
    this.cleanups.push(() => {
      form.removeEventListener("submit", handleSubmit);
      this.searchInput?.removeEventListener("input", handleInput);
    });
  };
  setupSidebarClicks = () => {
    if (!this.sidebar) {
      return;
    }
    const cleanup = delegate(this.sidebar.container, "[data-dealer-row]", "click", (event, element) => {
      if (event.target.closest("a")) {
        return;
      }
      this.focusDealer(element.dataset.dealerId ?? "");
    });
    this.cleanups.push(cleanup);
  };
  focusDealer = (id) => {
    const marker = this.markersById.get(id);
    if (!marker || !this.map) {
      return;
    }
    if (this.clusterGroup) {
      this.clusterGroup.zoomToShowLayer(marker, () => marker.openPopup());
      return;
    }
    this.map.setView(marker.getLatLng(), Math.max(this.map.getZoom(), this.settings?.maxZoomOnFit ?? 13), {
      animate: !this.prefersReducedMotion
    });
    marker.openPopup();
  };
  handleTabChange = (tab) => {
    if (!this.searchInput) {
      return;
    }
    if (tab === "map") {
      window.requestAnimationFrame(() => this.map?.invalidateSize());
    }
    if (tab === "webshop") {
      this.searchInput.placeholder = this.strings.searchPlaceholderWebshop;
      this.searchInput.value = this.webshopQuery;
      if (this.webshop) {
        filterByName({ rows: this.webshop.rows, query: this.webshopQuery });
      }
      return;
    }
    this.searchInput.placeholder = this.strings.searchPlaceholderGeo;
    this.searchInput.value = this.searchState.label;
  };
  runGeoSearch = async (query) => {
    try {
      const point = await geocodeQuery({ query, signal: this.abortController.signal });
      if (this.abortController.signal.aborted) {
        return;
      }
      if (!point) {
        this.showNotice(this.strings.searchError);
        return;
      }
      this.searchState = { point, label: query };
      this.applySearch();
    } catch (error) {
      if (error.name === "AbortError") {
        return;
      }
      logger.error("DealerMap: search geocoding failed", { query, error });
      this.showNotice(this.strings.searchError);
    }
  };
  clearSearch = () => {
    this.searchState = { point: null, label: "" };
    this.applySearch();
  };
  applySearch = () => {
    const { point, label } = this.searchState;
    this.hideNotice();
    if (!point) {
      for (const collection of [this.sidebar, this.list]) {
        if (collection) {
          resetDistances(collection);
        }
      }
      if (this.headingEl) {
        this.headingEl.textContent = this.strings.resultsHeadingAll;
      }
      this.listPage = 1;
      this.listOrder = this.list ? alphabeticalIds(this.list.rows) : [];
      this.renderListPage();
      this.removeSearchMarker();
      this.fitToPoints(Array.from(this.resolvedPoints.values()));
      return;
    }
    const locale = document.documentElement.lang || "da";
    let matched = [];
    let usedNearestFallback = false;
    for (const collection of [this.sidebar, this.list]) {
      if (!collection) {
        continue;
      }
      const result = applyDistances({
        ...collection,
        points: this.resolvedPoints,
        origin: point,
        radiusKm: this.settings?.radiusKm ?? DEFAULT_RADIUS_KM,
        locale
      });
      matched = result.matched;
      usedNearestFallback = result.usedNearestFallback;
    }
    if (this.headingEl) {
      this.headingEl.textContent = this.strings.resultsHeading.replace("[count]", String(matched.length)).replace("[query]", label);
    }
    if (usedNearestFallback) {
      this.showNotice(this.strings.noResults.replace("[radius]", String(this.settings?.radiusKm ?? DEFAULT_RADIUS_KM)).replace("[count]", String(NEAREST_FALLBACK_COUNT)));
    }
    this.listPage = 1;
    this.listOrder = matched;
    this.renderListPage();
    this.placeSearchMarker(point);
    const matchedPoints = matched.map((id) => this.resolvedPoints.get(id)).filter((p) => Boolean(p));
    this.fitToPoints([point, ...matchedPoints]);
  };
  renderListPage = () => {
    if (!this.list) {
      return;
    }
    const pageSize = Math.max(1, this.settings?.listPageSize ?? DEFAULT_LIST_PAGE_SIZE);
    const totalPages = Math.max(1, Math.ceil(this.listOrder.length / pageSize));
    this.listPage = Math.min(Math.max(1, this.listPage), totalPages);
    const visible = new Set(this.listOrder.slice((this.listPage - 1) * pageSize, this.listPage * pageSize));
    for (const row of this.list.rows) {
      row.element.hidden = !visible.has(row.id);
    }
    if (this.pagerNav) {
      renderPager({
        nav: this.pagerNav,
        page: this.listPage,
        totalPages,
        strings: {
          previous: this.strings.paginationPrevious,
          next: this.strings.paginationNext,
          page: this.strings.paginationPage
        },
        onNavigate: this.handlePageChange
      });
    }
  };
  handlePageChange = (page) => {
    this.listPage = page;
    this.renderListPage();
    if (this.list && this.list.container.getBoundingClientRect().top < 0) {
      this.list.container.scrollIntoView({
        block: "start",
        behavior: this.prefersReducedMotion ? "auto" : "smooth"
      });
    }
  };
  placeSearchMarker = (point) => {
    if (!this.map) {
      return;
    }
    this.removeSearchMarker();
    this.searchMarker = L.marker([point.lat, point.lng], {
      icon: createPinIcon({ variant: "search" }),
      interactive: false,
      keyboard: false
    }).addTo(this.map);
  };
  removeSearchMarker = () => {
    this.searchMarker?.remove();
    this.searchMarker = null;
  };
  fitToPoints = (points) => {
    if (!this.map || !this.settings?.fitBounds || !points.length) {
      return;
    }
    this.map.fitBounds(points.map((p) => [p.lat, p.lng]), {
      padding: [40, 40],
      maxZoom: this.settings.maxZoomOnFit,
      animate: !this.prefersReducedMotion
    });
  };
  showNotice = (text) => {
    if (this.noticeEl) {
      this.noticeEl.textContent = text;
      this.noticeEl.hidden = false;
    }
  };
  hideNotice = () => {
    if (this.noticeEl) {
      this.noticeEl.hidden = true;
    }
  };
  showLoadingError = () => {
    if (!this.loadingEl) {
      return;
    }
    this.loadingEl.classList.add("dealer-map__loading--error");
    this.loadingEl.textContent = this.loadingEl.dataset.errorText ?? "";
  };
  createMap = () => {
    if (!this.canvas || !this.settings) {
      return;
    }
    this.loadingEl?.remove();
    this.loadingEl = null;
    const { settings } = this;
    this.map = L.map(this.canvas, {
      scrollWheelZoom: settings.scrollWheelZoom,
      attributionControl: true,
      zoomAnimation: !this.prefersReducedMotion,
      fadeAnimation: !this.prefersReducedMotion,
      markerZoomAnimation: !this.prefersReducedMotion
    });
    this.map.attributionControl.setPrefix(false);
    this.map.setView([settings.initialLat, settings.initialLng], settings.initialZoom, {
      animate: !this.prefersReducedMotion
    });
    const subdomains = settings.tileSubdomains ? settings.tileSubdomains.split("") : ["a", "b", "c"];
    L.tileLayer(settings.tileUrl, {
      subdomains,
      attribution: settings.tileAttribution,
      maxZoom: 19,
      detectRetina: false
    }).addTo(this.map);
    if (typeof L.markerClusterGroup === "function") {
      this.clusterGroup = L.markerClusterGroup({
        iconCreateFunction: createClusterIcon,
        showCoverageOnHover: false,
        animate: !this.prefersReducedMotion
      });
      this.map.addLayer(this.clusterGroup);
    }
    this.resizeObserver = new ResizeObserver(
      debounce(() => {
        this.map?.invalidateSize();
      }, 150)
    );
    this.resizeObserver.observe(this.canvas);
  };
  addMarkers = async () => {
    if (!this.map || !this.settings) {
      return;
    }
    const icon = createPinIcon();
    const canHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    const points = [];
    for (const dealer of this.dealers) {
      const point = await resolveDealer(dealer, this.abortController.signal);
      if (this.abortController.signal.aborted || !this.map) {
        return;
      }
      if (!point) {
        continue;
      }
      const marker = L.marker([point.lat, point.lng], { icon });
      if (this.clusterGroup) {
        this.clusterGroup.addLayer(marker);
      } else {
        marker.addTo(this.map);
      }
      const address = formatAddress(dealer);
      if (canHover) {
        marker.bindTooltip(this.buildTooltip(dealer), { direction: "top", opacity: 1, className: "dealer-map__tooltip" });
        marker.on("popupopen", () => marker.closeTooltip());
      }
      marker.bindPopup(this.buildPopup(dealer), {
        closeButton: true,
        autoPan: true,
        autoPanPadding: [24, 24],
        className: "dealer-map__popup",
        maxWidth: 280
      });
      const element = marker.getElement();
      element?.setAttribute("role", "button");
      element?.setAttribute("aria-label", address ? `${dealer.name}, ${address}` : dealer.name);
      this.markersById.set(dealer.id, marker);
      this.resolvedPoints.set(dealer.id, point);
      points.push(point);
    }
    if (this.searchState.point) {
      this.applySearch();
    } else {
      this.fitToPoints(points);
    }
  };
  buildTooltip = (dealer) => {
    const root = document.createElement("div");
    const name = document.createElement("span");
    name.className = "dealer-map__tooltip-name";
    name.textContent = dealer.name;
    root.append(name);
    const address = formatAddress(dealer);
    if (address) {
      const line = document.createElement("span");
      line.className = "dealer-map__tooltip-address";
      line.textContent = address;
      root.append(line);
    }
    return root;
  };
  buildPopup = (dealer) => {
    const settings = this.settings;
    const root = document.createElement("div");
    root.className = "dealer-map__popup-inner";
    const name = document.createElement("p");
    name.className = "dealer-map__popup-name";
    name.textContent = dealer.name;
    root.append(name);
    const address = formatAddress(dealer);
    if (address) {
      const line = document.createElement("p");
      line.className = "dealer-map__popup-address";
      line.textContent = address;
      root.append(line);
    }
    if (settings.showOpeningHours && dealer.openingHours) {
      const hours = document.createElement("p");
      hours.className = "dealer-map__popup-hours";
      hours.textContent = dealer.openingHours;
      root.append(hours);
    }
    if (settings.showPhone && dealer.phone) {
      root.append(this.buildLink(`tel:${dealer.phone.replace(/\s/g, "")}`, dealer.phone));
    }
    if (settings.showEmail && dealer.email) {
      root.append(this.buildLink(`mailto:${dealer.email}`, dealer.email));
    }
    if (settings.showWebsite && dealer.website) {
      const link = this.buildLink(dealer.website, dealer.website.replace(/^https?:\/\//, "").replace(/\/$/, ""));
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      root.append(link);
    }
    const directions = document.createElement("a");
    directions.href = buildDirectionsUrl(dealer);
    directions.target = "_blank";
    directions.rel = "noopener noreferrer";
    directions.className = `btn ${settings.directionsStyle} dealer-map__directions`;
    const directionsLabel = document.createElement("span");
    directionsLabel.className = "btn__text";
    directionsLabel.textContent = settings.directionsLabel;
    directions.append(directionsLabel);
    root.append(directions);
    return root;
  };
  buildLink = (href, label) => {
    const link = document.createElement("a");
    link.href = href;
    link.textContent = label;
    link.className = "dealer-map__popup-link";
    return link;
  };
}
class ProductDescription extends HTMLElement {
  static CLAMP_CLASS = "text-limit-5-lines";
  content = null;
  toggle = null;
  moreLabel = null;
  lessLabel = null;
  isExpanded = false;
  resizeObserver = null;
  connectedCallback() {
    this.content = this.querySelector("[data-description-content]");
    this.toggle = this.querySelector("[data-description-toggle]");
    this.moreLabel = this.querySelector("[data-description-more]");
    this.lessLabel = this.querySelector("[data-description-less]");
    if (!this.content || !this.toggle) {
      logger.warn("ProductDescription: missing content or toggle element");
      return;
    }
    this.toggle.addEventListener("click", this.handleToggle);
    this.resizeObserver = new ResizeObserver(this.syncOverflowState);
    this.resizeObserver.observe(this.content);
  }
  disconnectedCallback() {
    this.toggle?.removeEventListener("click", this.handleToggle);
    this.resizeObserver?.disconnect();
  }
  /**
   * Shows or hides the toggle based on whether the clamped content overflows.
   * Skips the check while the description is expanded so we don't hide the
   * "read less" button on resize.
   */
  syncOverflowState = () => {
    if (!this.content || !this.toggle || this.isExpanded) {
      return;
    }
    const overflows = this.content.scrollHeight > this.content.clientHeight + 1;
    this.toggle.hidden = !overflows;
  };
  handleToggle = () => {
    if (!this.content || !this.toggle) {
      return;
    }
    this.isExpanded = !this.isExpanded;
    this.content.classList.toggle(ProductDescription.CLAMP_CLASS, !this.isExpanded);
    this.toggle.setAttribute("aria-expanded", String(this.isExpanded));
    if (this.moreLabel) {
      this.moreLabel.hidden = this.isExpanded;
    }
    if (this.lessLabel) {
      this.lessLabel.hidden = !this.isExpanded;
    }
  };
}
const scripts = new ThemeScripts();
const dataLayer = new DataLayer();
scripts.load({
  id: "gtm",
  strategy: "on-default-consent-set",
  gtmConfig: { containerId: "GTM-TRWNRH2R" }
});
dataLayer.init();
customElements.define("cookie-banner", CookieBanner);
customElements.define("product-description", ProductDescription);
customElements.define("dealer-map", DealerMap);
